import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import {
  Button,
  Field,
  InlineAlert,
  Page,
  PageHeader,
  QueryState,
} from '@aeris/ui'

import {
  fetchBranding,
  updateBranding,
  uploadBrandingLogo,
  type BrandingResponseType,
} from '../lib/api.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  READABLE_ON_WHITE_TEXT,
  contrastRatio,
  isReadableWithWhiteText,
  paletteFrom,
} from '../lib/brand-color.ts'

/**
 * A marca da loja (F169).
 *
 * ## Por que esta tela existe antes do primeiro cliente de vertical
 *
 * Ela é a primeira usuária da chave por conta: `aparencia` tem
 * `feature: 'marca-propria'` em `PAGES` e em `MODULES`, e a loja sem a chave
 * não a vê no menu, no Ctrl+K, no Início nem no Modo PC — e a URL colada é
 * recusada. O mecanismo das telas por conta nasce com um usuário real em vez de
 * esperar.
 *
 * ## A prévia é o que resolve "a logo sumiu"
 *
 * A logo do cliente vai viver sobre o fundo do menu, que é escuro. Uma logo
 * escura desaparece ali, e nenhum aviso escrito ensina isso tão rápido quanto
 * ver. Mesmo molde das telas de modelo de etiqueta e de nota: o que se vê é o
 * que sai.
 *
 * ## A cor é recusada quando não dá para ler
 *
 * Os botões primários levam texto branco. Abaixo de 4,5:1 — o mínimo da WCAG
 * para texto normal, o mesmo piso que `tokens.css` anota em cada cor crua —
 * uma loja escolheria amarelo-claro e descobriria depois que ninguém enxerga
 * os botões dela.
 */

const DEFAULT_COLOR = '#0064ec'

export const AparenciaPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const tenantId = session?.context?.tenantId ?? null

  const fileInput = useRef<HTMLInputElement>(null)
  const [color, setColor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['branding', tenantId],
    queryFn: ({ signal }) => fetchBranding(signal),
    enabled: tenantId !== null,
  })

  const branding = query.data?.branding ?? null
  const version = query.data?.version ?? null

  /*
   * O que está na tela: o que foi digitado agora, senão o que está gravado.
   * `null` em `color` significa "ainda não mexi", e não "sem cor".
   */
  const current = color ?? branding?.primaryColor ?? DEFAULT_COLOR
  const palette = paletteFrom(current)
  const ratio = contrastRatio(current, '#ffffff')
  const readable = isReadableWithWhiteText(current)

  const report = (cause: unknown): void => {
    setError(
      cause instanceof ApiError
        ? cause.message
        : 'Não foi possível salvar. Tente novamente.'
    )
  }

  const settle = (next: BrandingResponseType): void => {
    queryClient.setQueryData(['branding', tenantId], next)
    setColor(null)
    setError(null)
  }

  const save = useMutation({
    mutationFn: async (input: { primaryColor: string | null }) =>
      await updateBranding({
        primaryColor: input.primaryColor,
        clearLogo: false,
        version,
      }),
    onSuccess: settle,
    onError: report,
  })

  const clearLogo = useMutation({
    mutationFn: async () =>
      await updateBranding({
        primaryColor: branding?.primaryColor ?? null,
        clearLogo: true,
        version,
      }),
    onSuccess: settle,
    onError: report,
  })

  const upload = useMutation({
    mutationFn: async (file: File) => await uploadBrandingLogo(file),
    onSuccess: settle,
    onError: report,
  })

  const logoUrl =
    branding?.logoKey === null || branding?.logoKey === undefined
      ? null
      : `/api/v1/organization/branding/logo?v=${String(version ?? 0)}`

  return (
    <Page>
      <PageHeader
        title="Aparência"
        description="A logo e a cor desta loja, no lugar da marca Aeris One."
      />

      {error !== null && (
        <InlineAlert tone="danger" title="Não deu certo">
          {error}
        </InlineAlert>
      )}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        what="a marca da loja"
        isEmpty={false}
        empty={null}
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-3 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4">
              <h2 className="text-(length:--text-sm) font-(--weight-medium)">
                Logo
              </h2>
              <p className="text-(length:--text-xs) text-(--text-muted)">
                PNG com fundo transparente funciona melhor. Ela aparece sobre o
                menu escuro — confira na prévia ao lado antes de salvar.
              </p>

              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file !== undefined) upload.mutate(file)
                  // Permite subir o MESMO arquivo de novo depois de corrigi-lo:
                  // sem isto, o `change` não dispara na segunda vez.
                  event.target.value = ''
                }}
              />

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  leadingIcon={<Upload aria-hidden className="h-4 w-4" />}
                  loading={upload.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  {logoUrl === null ? 'Enviar logo' : 'Trocar logo'}
                </Button>

                {logoUrl !== null && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={clearLogo.isPending}
                    onClick={() => {
                      clearLogo.mutate()
                    }}
                  >
                    Voltar para a logo do Aeris
                  </Button>
                )}
              </div>
            </section>

            <section className="flex flex-col gap-3 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4">
              <h2 className="text-(length:--text-sm) font-(--weight-medium)">
                Cor da marca
              </h2>

              <Field
                label="Cor"
                hint="O seletor do sistema abre ao clicar no quadrado."
              >
                {(field) => (
                  <div className="flex items-center gap-3">
                    <input
                      {...field}
                      type="color"
                      value={current}
                      className="h-9 w-16 cursor-pointer rounded-(--radius-sm) border border-(--border-default) bg-(--surface)"
                      onChange={(event) => {
                        setColor(event.target.value)
                      }}
                    />
                    <code className="text-(length:--text-sm)">{current}</code>
                  </div>
                )}
              </Field>

              {!readable && (
                <InlineAlert tone="warning" title="Esta cor não dá para ler">
                  Os botões do sistema levam texto branco, e esta cor deixa o
                  texto com contraste de{' '}
                  {ratio === null ? '—' : ratio.toFixed(1)}:1. O mínimo é{' '}
                  {READABLE_ON_WHITE_TEXT.toFixed(1)}:1. Escolha um tom mais
                  escuro.
                </InlineAlert>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={!readable}
                  loading={save.isPending}
                  onClick={() => {
                    save.mutate({ primaryColor: current })
                  }}
                >
                  Salvar cor
                </Button>

                {branding?.primaryColor !== null && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      save.mutate({ primaryColor: null })
                    }}
                  >
                    Voltar para a cor do Aeris
                  </Button>
                )}
              </div>
            </section>
          </div>

          {/*
            A prévia desenha o menu de verdade: o mesmo fundo escuro derivado
            da cor, a logo como ela vai sair, e um botão primário. É onde se vê
            que uma logo escura some.
          */}
          <section className="flex flex-col gap-3">
            <h2 className="text-(length:--text-sm) font-(--weight-medium)">
              Prévia
            </h2>

            <div
              className="flex flex-col gap-4 rounded-(--radius-lg) p-4"
              style={{ background: palette?.sidebarBg ?? 'var(--sidebar-bg)' }}
            >
              <div className="flex h-12 items-center">
                {logoUrl === null ? (
                  <span className="text-(length:--text-sm) text-white opacity-70">
                    Sem logo — o sistema usa a marca Aeris One
                  </span>
                ) : (
                  <img
                    src={logoUrl}
                    alt="Prévia da logo da loja"
                    className="h-10 w-auto object-contain"
                  />
                )}
              </div>

              <div
                className="rounded-(--radius-sm) px-3 py-2 text-(length:--text-sm) text-white"
                style={{
                  background: palette?.primary ?? 'var(--brand-primary)',
                }}
              >
                Item ativo do menu
              </div>
            </div>

            <div className="rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4">
              <button
                type="button"
                className="rounded-(--radius-sm) px-3 py-2 text-(length:--text-sm) text-white"
                style={{
                  background: palette?.primary ?? 'var(--brand-primary)',
                }}
              >
                Um botão do sistema
              </button>
            </div>
          </section>
        </div>
      </QueryState>
    </Page>
  )
}
